import React, { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react';
import {
	useTable,
	useGlobalFilter,
	useSortBy,
	usePagination,
	TableOptions,
	useAsyncDebounce,
	useBlockLayout,
	SortingRule,
	useExpanded,
} from 'react-table';
import { useTranslation } from 'react-i18next';
import { Form, Card, Table, Pagination, Row, Col } from 'react-bootstrap';
import { FaMinus, FaSortDown, FaSortUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './generic-table.scss';
import TableSkeleton from '../TableSkeleton/TableSkeleton';

const globalFilter = (rows, columnIds, filterValue) => {
	return rows.filter((row) => {
		const rowValues = columnIds.map((id) => {
			const cellValue = row.values[id];
			return cellValue ? cellValue.toString().toLowerCase().trim() : '';
		});

		const snakeCaseFilterValue = filterValue
			.toLowerCase()
			.trim()
			.replaceAll(' ', '_');

		return rowValues.some(
			(value) =>
				value.includes(filterValue) || value.includes(snakeCaseFilterValue)
		);
	});
};

const TableContainer = ({
	dblClickCallback = (row: any, rowObject?: any, toggleRowExpanded?: any) => { },
	useFixedColumn = false,
	pageIndex = 0,
	className = '',
	columns,
	data,
	loading,
	error,
	hasFilter = true,
	filterSlot = null,
	initPageSize = 10,
	noBorder = false,
	isMinorPagination = false,
	dbClickType = 'double',
	onSortChange = (rule: SortingRule<Object>[]) => { },
	onFilteredRowsChange = (rows: any[]) => { },
	onGlobalFilterChange = (filter: string | undefined) => { },
	showModalFunc = (type: any, params?: any) => { },
	isExpandable = false,
	renderRowSubComponent = (row: any) => <></>,
	hover = true,
	rowProps = (row: any) => ({}),
	skipPageResetRef = undefined,
	...props
}: any & {skipPageResetRef?:  MutableRefObject<boolean | undefined> | null}) => {
	const { t } = useTranslation()
	const tableOptions: TableOptions<Object> = {
		columns,
		data,
		autoResetPage: false,
		autoResetGlobalFilter: false,
		initialState: {
			pageSize: initPageSize,
			hiddenColumns: ['id'],
			...(props.sortBy ? { sortBy: props.sortBy } : {}),
			...(props.initialGlobalFilter ? { globalFilter: props.initialGlobalFilter } : {}),
		},
		globalFilter,
		// @ts-ignore
		autoResetPageIndex: skipPageResetRef ? !skipPageResetRef.current : undefined,
		// @ts-ignore
		autoResetExpanded: skipPageResetRef ? !skipPageResetRef.current : undefined,
		...props,
	};

	useEffect(() => {
		if (skipPageResetRef) {
		// @ts-ignore
			skipPageResetRef.current = false
		}
	})

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows, // non si usa perchè ormai le rows stanno in page
		prepareRow,
		state,
		preGlobalFilteredRows,
		setGlobalFilter,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		visibleColumns,
		toggleRowExpanded,
	} = useTable(
		tableOptions,
		useGlobalFilter,
		useSortBy,
		useFixedColumn ? useBlockLayout : () => { },
		isExpandable ? useExpanded : () => { },
		usePagination,
		// useFilters,
	);

	const [value, setValue] = useState(state.globalFilter ?? props.initialGlobalFilter ?? '');

	const onChange = useAsyncDebounce((value) => {
		gotoPage(0);
		setGlobalFilter(value || undefined);
	}, 200);

	useEffect(() => {
		if (onSortChange) {
			// @ts-ignore
			onSortChange(state.sortBy);
		}
	}, [state.sortBy])

	useEffect(() => {
		onFilteredRowsChange(rows.map((r) => r.original));
	}, [rows]);
	const isFirstFilterChange = useRef(true);

	useEffect(() => {
		if (isFirstFilterChange.current) {
			isFirstFilterChange.current = false;
			return;
		}
		onGlobalFilterChange(state.globalFilter);
	}, [state.globalFilter])

	return (
		<>
			{(hasFilter || filterSlot) && (
				<Row className="mt-4 align-items-center">
					{hasFilter && (
						<Col md={6} xl={4}>
							<Form.Control
								size="lg"
								placeholder={t('search') + '...'}
								type="search"
								value={value}
								onChange={(e) => {
									setValue(e.target.value);
									onChange(e.target.value);
								}}
							/>
						</Col>
					)}
					{filterSlot && <Col md={6} xl={6} className="d-flex">{filterSlot}</Col>}
				</Row>
			)}

			{loading && (
				<TableSkeleton rows={initPageSize} />
			)}

			{!loading && !error && (
				<Row>
					<Col>
						<Card className={'mt-4' + (noBorder ? ' table-no-border' : '')}>
							<div className="flipped-container flipped">
								<Table
									striped
									borderless
									hover={hover}
									responsive
									{...getTableProps()}
									className={className + ' m-0 mb-1 mt-3'}
								>
									<thead className="border-bottom">
										{headerGroups.map((headerGroup) => (
											<tr {...headerGroup.getHeaderGroupProps()}>
												{headerGroup.headers.map((column: any) => (
													<th
														className="text-nowrap"
														{...column.getHeaderProps({
															...column.getSortByToggleProps({ title: undefined }),
															className: column.className,
														})}
													>
														{column.render('Header')}
														{column.canSort ? (
															<SortingControls column={column} />
														) : null}
													</th>
												))}
											</tr>
										))}
									</thead>
									<tbody {...getTableBodyProps()}>
										{page.map((row) => {
											prepareRow(row);
											return (
												<Fragment key={row.id}>
													<tr
														className="role-button"
														onClick={() => {
															dbClickType == 'single' && dblClickCallback && dblClickCallback(row.original, row, toggleRowExpanded)
														}}
														onDoubleClick={() =>
															dbClickType == 'double' && dblClickCallback && dblClickCallback(row.original, row, toggleRowExpanded)
														}
														{...row.getRowProps(rowProps(row))}
													>
														{row.cells.map((cell: any) => (
															<td
																className="text-nowrap"
																{...cell.getCellProps({
																	className: cell.column.className,
																})}
															>
																{cell.column.type === 'document' ? (
																	cell.value.length ?
																		<Link
																			to="#"
																			onClick={(e) => {
																				e.preventDefault();
																				cell.value.length &&
																					showModalFunc('offerDocuments', cell.value);
																			}}
																		> {cell.render('Cell')} </Link> : <FaMinus />) : cell.render('Cell')
																}
															</td>
														))}
													</tr>
													{
														row.isExpanded ? (
															<tr>
																<td colSpan={visibleColumns.length}>
																	{renderRowSubComponent?.({ row })}
																</td>
															</tr>
														) : null
													}
												</Fragment>
											);
										})}
									</tbody>
								</Table>
							</div>

						</Card>

						{(!isMinorPagination && rows?.length > 0) ? (
							<div className="mt-2 float-end">
								<Pagination>
									<Form.Select
										className="w-auto page-select"
										value={state.pageSize}
										onChange={(e) => {
											setPageSize(Number(e.target.value));
										}}
									>
										{[2, 5, 10, 20].map((pageSize) => (
											<option key={pageSize} value={pageSize}>
												{t('show')} {pageSize}
											</option>
										))}
									</Form.Select>

									<Form.Label className="text-nowrap mx-3 mt-2">
										{t('page')}
										<strong className="ms-1">
											{state.pageIndex + 1} di {pageOptions.length}
										</strong>
									</Form.Label>

									<Pagination.First
										onClick={() => gotoPage(0)}
										disabled={!canPreviousPage}
									/>
									<Pagination.Prev
										onClick={() => previousPage()}
										disabled={!canPreviousPage}
									/>
									<Pagination.Next
										onClick={() => nextPage()}
										disabled={!canNextPage}
									/>
									<Pagination.Last
										onClick={() => gotoPage(pageCount - 1)}
										disabled={!canNextPage}
									/>
								</Pagination>
							</div>
						) : // <div className="mt-1 float-end minor-pagination-wrapper">
							// 	<Pagination className="minor-pagination">
							// 		<Form.Label className="text-nowrap page-labels">
							// 			Page
							// 			<strong className="ms-1">
							// 				{state.pageIndex + 1} of {pageOptions.length}
							// 			</strong>
							// 		</Form.Label>
							// 		<Pagination.First
							// 			onClick={() => gotoPage(0)}
							// 			disabled={!canPreviousPage}
							// 		/>
							// 		<Pagination.Prev
							// 			onClick={() => previousPage()}
							// 			disabled={!canPreviousPage}
							// 		/>
							// 		<Pagination.Next
							// 			onClick={() => nextPage()}
							// 			disabled={!canNextPage}
							// 		/>
							// 		<Pagination.Last
							// 			onClick={() => gotoPage(pageCount - 1)}
							// 			disabled={!canNextPage}
							// 		/>
							// 	</Pagination>
							// </div>
							null}
					</Col>
				</Row>
			)}
		</>
	);
};

export default TableContainer;

const SortingControls = ({ column }) => {
	return (
		<span className="position-relative">
			{column.isSorted ? (
				column.isSortedDesc ? (
					<>
						<FaSortUp className="mt-1 opacity-25 position-absolute" />
						<FaSortDown className="mt-1 position-absolute" />
					</>
				) : (
					<>
						<FaSortUp className="mt-1 position-absolute" />
						<FaSortDown className="mt-1 opacity-25 position-absolute" />
					</>
				)
			) : (
				<>
					<FaSortUp className="mt-1 opacity-25 position-absolute" />
					<FaSortDown className="mt-1 opacity-25 position-absolute" />
				</>
			)}
		</span>
	);
};
